import { motion } from "framer-motion";
import { Bus, ShoppingCart, Trees, Flower2 } from "lucide-react";
import { useLang } from "@/i18n";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" }, transition: { duration: 0.8, delay },
});

const NEARBY_ICONS = [Bus, ShoppingCart, Flower2, Trees];

export default function RasnovStory() {
  const { t } = useLang();
  const nearby = t("story2.nearby");

  return (
    <section className="py-24 md:py-32 bg-cream-light border-y border-warm-grey/30" data-testid="rasnov-story">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.p {...fadeUp()} className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3">
          <span className="inline-block w-10 h-px bg-terracotta" />
          {t("story2.eyebrow")}
        </motion.p>
        <motion.h2 {...fadeUp(0.1)} className="mt-5 font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-main max-w-3xl">
          {t("story2.title")}
        </motion.h2>
        <motion.p {...fadeUp(0.15)} className="mt-6 text-base md:text-lg text-brown font-light leading-relaxed max-w-3xl" data-testid="story-lead">
          {t("story2.lead")}
        </motion.p>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 items-start">
          <motion.div {...fadeUp(0.2)} className="lg:col-span-7 space-y-6">
            {[t("story2.p1"), t("story2.p2"), t("story2.p3")].map((p, i) => (
              <p key={i} className="text-brown/85 font-light leading-relaxed" data-testid={`story-p${i + 1}`}>{p}</p>
            ))}
            <p className="font-serif italic text-xl text-green-dark" data-testid="story-punch">{t("story2.p4")}</p>

            <div className="mt-8 bg-cream border border-warm-grey/40 rounded-3xl p-7" data-testid="story-nearby">
              <p className="text-xs tracking-[0.2em] uppercase font-bold text-walnut">{t("story2.nearby_title")}</p>
              <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                {nearby.map((n, i) => {
                  const Icon = NEARBY_ICONS[i] || Trees;
                  return (
                    <li key={i} className="flex items-center gap-3 text-sm text-brown/90 font-light">
                      <Icon size={16} className="text-terracotta shrink-0" /> {n}
                    </li>
                  );
                })}
              </ul>
            </div>
          </motion.div>

          <motion.div {...fadeUp(0.25)} className="lg:col-span-5">
            <div className="overflow-hidden rounded-3xl border border-warm-grey/40 shadow-xl shadow-main/5">
              <img src="/assets/cetatea-rasnov-aerial.jpg" alt="Cetatea Râșnov vedere aeriană" loading="lazy"
                className="w-full h-auto" data-testid="story-aerial" />
            </div>
          </motion.div>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-8">
          {[
            { src: "/assets/harta-relaxarii.jpg", cap: t("story2.map1_caption"), testid: "story-map-relaxare" },
            { src: "/assets/harta-experientelor.jpg", cap: t("story2.map2_caption"), testid: "story-map-experiente" },
          ].map((m) => (
            <motion.figure key={m.src} {...fadeUp(0.2)} className="group">
              <div className="overflow-hidden rounded-3xl border border-warm-grey/40 shadow-lg shadow-main/5">
                <img src={m.src} alt={m.cap} loading="lazy"
                  className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-700" data-testid={m.testid} />
              </div>
              <figcaption className="mt-3 text-sm text-brown/70 font-light text-center">{m.cap}</figcaption>
            </motion.figure>
          ))}
        </div>
        <motion.p {...fadeUp(0.2)} className="mt-6 text-xs text-brown/50 font-light text-center" data-testid="story-maps-note">
          {t("story2.maps_note")}
        </motion.p>
      </div>
    </section>
  );
}
